// app/frontend/src/utils/pageTheme.ts
//
// Turns a stored page theme ({ template_id, accent }) into the colors a page
// paints with (#2230 / #2231), and keeps the organizer's accent readable: it
// gets lightened until it clears WCAG AA on the template's background, and
// text on an accent fill becomes black or white, whichever reads.
import { COLORS } from '../constants/theme';
import { TEMPLATES, getTemplate, type Template, type TemplateId } from '../constants/pageThemes';

/** As stored on organizers.theme / events.theme. Empty or null: no theme. */
export interface PageThemeSettings {
  template_id?: TemplateId | string | null;
  accent?: string | null;
}

export interface ResolvedPageTheme {
  /** null when only an accent was picked: the page keeps the site background. */
  templateId: TemplateId | null;
  template: Template;
  /** Fills, borders, icons, focus rings: at least 3:1 on the background. */
  accent: string;
  /** Links and accent-colored text: at least 4.5:1 on the background. */
  accentText: string;
  /** Text and icons sitting on an accent fill. */
  onAccent: string;
  /** Text on an accent-tinted badge: at least 5:1 on badgeBackground() (#2321). */
  badgeText: string;
  /** Fill of a solid accent badge: the accent, darkened if needed for 5:1 text (#2321). */
  badgeSolid: string;
  /** Black or white text on badgeSolid, at least 5:1 (#2321). */
  badgeOnAccent: string;
  /** Solid fills that carry white text (active tab, tag chips): the accent, deepened until white clears AA (#2337). */
  whiteTextFill: string;
  /** Badge and chip fills that carry white text: the accent, deepened until white clears 5:1, orange included (#2431). */
  whiteBadgeFill: string;
  /** True when the picked accent had to be lightened to stay readable. */
  adjusted: boolean;
}

export const MIN_UI_CONTRAST = 3;
export const MIN_TEXT_CONTRAST = 4.5;
/** Badges are small type on a tint, so they're held above AA (#2321). */
export const MIN_BADGE_CONTRAST = 5;

const BLACK = '#111111';
const WHITE = '#ffffff';

/** "#abc" / "abc123" / "#ABC123" -> "#abc123"; null when it isn't a color. */
export function normalizeHex(value: string | null | undefined): string | null {
  let hex = (value ?? '').trim().toLowerCase();
  if (!hex) return null;
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex.slice(1).split('').map((c) => c + c).join('')}`;
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null;
}

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Lighten `color` toward white until it reaches `min` contrast on `background`.
 * Templates are all dark, so lighter always helps. Mixing toward white keeps
 * the hue, which a lightness bump in HSL does too, without a color-space trip.
 */
export function ensureContrast(color: string, background: string, min: number): string {
  if (contrastRatio(color, background) >= min) return color;
  const rgb = toRgb(color);
  for (let step = 1; step <= 20; step += 1) {
    const t = step / 20;
    const mixed = toHex(rgb.map((v) => v + (255 - v) * t) as [number, number, number]);
    if (contrastRatio(mixed, background) >= min) return mixed;
  }
  return WHITE;
}

/**
 * The lightest surface a pass badge sits on (#2321): the accent at 30% over a
 * selected tier row (card + white wash), which covers the badge's own 14-16%
 * tint and the row's 12% under it. Badge text is checked against this.
 */
export function badgeBackground(accent: string): string {
  const row = toRgb('#323236');
  const tint = toRgb(normalizeHex(accent) ?? COLORS.orange);
  return toHex(row.map((v, i) => v + (tint[i] - v) * 0.3) as [number, number, number]);
}

/** Black or white, whichever has more contrast on the fill. */
function strongestOn(fill: string): string {
  return contrastRatio(BLACK, fill) >= contrastRatio(WHITE, fill) ? BLACK : WHITE;
}

/**
 * A solid badge fill that holds 5:1 text (#2321). Most accents already do with
 * black or white on them; a mid-tone one (#777) can't with either, so it's
 * darkened toward black until white text clears the bar.
 */
function badgeSolidFill(accent: string): string {
  if (contrastRatio(strongestOn(accent), accent) >= MIN_BADGE_CONTRAST) return accent;
  const rgb = toRgb(accent);
  for (let step = 1; step <= 20; step += 1) {
    const mixed = toHex(rgb.map((v) => v * (1 - step / 20)) as [number, number, number]);
    if (contrastRatio(WHITE, mixed) >= MIN_BADGE_CONTRAST) return mixed;
  }
  return BLACK;
}

/**
 * A solid accent fill that carries white text (#2337): the active tab, tag
 * and dance-style chips. The site is dark, so text on these stays white; a
 * light accent (lavender, 2.7:1 with white) is deepened toward black until
 * white reads, and a deep one is used as is. WCWD orange is the house look
 * and stays as it is.
 */
export function whiteTextFill(accent: string): string {
  if (normalizeHex(accent) === normalizeHex(COLORS.orange)) return accent;
  if (contrastRatio(WHITE, accent) >= MIN_TEXT_CONTRAST) return accent;
  const rgb = toRgb(accent);
  for (let step = 1; step <= 20; step += 1) {
    const mixed = toHex(rgb.map((v) => v * (1 - step / 20)) as [number, number, number]);
    if (contrastRatio(WHITE, mixed) >= MIN_TEXT_CONTRAST) return mixed;
  }
  return BLACK;
}

/**
 * A solid badge or chip fill that carries white text at 5:1 (#2431). Like
 * whiteTextFill, but badges are small type, so the bar is MIN_BADGE_CONTRAST
 * and WCWD orange (3.6:1 with white) deepens too. Buttons and tabs keep
 * whiteTextFill and the house orange.
 */
export function whiteBadgeFill(accent: string): string {
  const hex = normalizeHex(accent) ?? COLORS.orange;
  if (contrastRatio(WHITE, hex) >= MIN_BADGE_CONTRAST) return hex;
  const rgb = toRgb(hex);
  for (let step = 1; step <= 20; step += 1) {
    const mixed = toHex(rgb.map((v) => v * (1 - step / 20)) as [number, number, number]);
    if (contrastRatio(WHITE, mixed) >= MIN_BADGE_CONTRAST) return mixed;
  }
  return BLACK;
}

/** White if it reads on the fill, else black; whichever is stronger if neither clears AA. */
export function textOn(fill: string): string {
  const white = contrastRatio(WHITE, fill);
  if (white >= MIN_TEXT_CONTRAST) return WHITE;
  const black = contrastRatio(BLACK, fill);
  return black >= white ? BLACK : WHITE;
}

/** The site's own look, for an accent-only theme (no template picked). */
export const SITE_TEMPLATE: Template = {
  name: 'WhereCanWeDance',
  preview: COLORS.pageBg,
  background: COLORS.pageBg,
  cardBackground: COLORS.sectionBg,
  textColor: COLORS.textPrimary,
  accentColor: COLORS.orange,
  borderColor: COLORS.whiteAlpha10,
  baseColor: COLORS.pageBg,
  accents: ['#ef4444', '#ec4899', '#a855f7', '#3b82f6', '#06b6d4', '#22c55e', '#facc15'],
};

const isTemplateId = (id: unknown): id is TemplateId =>
  typeof id === 'string' && Object.prototype.hasOwnProperty.call(TEMPLATES, id);

/** null when there's nothing to theme: the page keeps WCWD orange. */
export function resolvePageTheme(settings: PageThemeSettings | null | undefined): ResolvedPageTheme | null {
  if (!settings) return null;
  const picked = normalizeHex(settings.accent);
  const templateId = isTemplateId(settings.template_id) ? settings.template_id : null;
  if (!templateId && !picked) return null;

  const template = templateId ? TEMPLATES[templateId] : SITE_TEMPLATE;
  const base = template.baseColor;
  const wanted = picked ?? template.accentColor;
  const accent = ensureContrast(wanted, base, MIN_UI_CONTRAST);

  return {
    templateId,
    template,
    accent,
    accentText: ensureContrast(wanted, base, MIN_TEXT_CONTRAST),
    onAccent: textOn(accent),
    badgeText: ensureContrast(ensureContrast(wanted, base, MIN_TEXT_CONTRAST), badgeBackground(accent), MIN_BADGE_CONTRAST),
    badgeSolid: badgeSolidFill(accent),
    badgeOnAccent: strongestOn(badgeSolidFill(accent)),
    whiteTextFill: whiteTextFill(accent),
    whiteBadgeFill: whiteBadgeFill(accent),
    adjusted: !!picked && accent !== picked,
  };
}

/**
 * A template with the organizer's accent applied (#2232), for code that paints
 * from a Template object (the Dance Card). The accent passes the contrast
 * check, and `onAccentColor` is the readable text color on it. An unknown id
 * falls back to the default template, as getTemplate does.
 */
export function themedTemplate(templateId: string | null | undefined, accent?: string | null): Template {
  const template = getTemplate(templateId ?? '');
  const id = isTemplateId(templateId) ? templateId : 'midnight-gradient';
  const resolved = resolvePageTheme({ template_id: id, accent })!;
  return { ...template, accentColor: resolved.accent, onAccentColor: resolved.onAccent };
}

/** The CSS custom properties a themed page reads. */
export function pageThemeVars(theme: ResolvedPageTheme): Record<string, string> {
  return {
    '--theme-accent': theme.accent,
    // Hover / pressed fill: the accent a step darker, like orange -> orangeHover.
    '--theme-accent-hover': `color-mix(in srgb, ${theme.accent} 84%, black)`,
    // Deeper and softer steps for multi-stop gradients (avatar ring, progress bars).
    '--theme-accent-deep': `color-mix(in srgb, ${theme.accent} 68%, black)`,
    '--theme-accent-deeper': `color-mix(in srgb, ${theme.accent} 52%, black)`,
    '--theme-accent-soft': `color-mix(in srgb, ${theme.accent} 55%, white)`,
    '--theme-accent-text': theme.accentText,
    '--theme-on-accent': theme.onAccent,
    '--theme-badge-text': theme.badgeText,
    '--theme-badge-solid': theme.badgeSolid,
    '--theme-badge-on-accent': theme.badgeOnAccent,
    '--theme-white-text-fill': theme.whiteTextFill,
    '--theme-white-badge-fill': theme.whiteBadgeFill,
    '--theme-bg': theme.template.background,
    '--theme-base': theme.template.baseColor,
    '--theme-card-bg': theme.template.cardBackground,
    '--theme-border': theme.template.borderColor,
    '--theme-text': theme.template.textColor,
  };
}

/**
 * One listed event's own colors (#2295). Its calendar bar, day-list row or
 * card sets these on its own element, so it wears its theme even on a page
 * themed for someone else. The --wcwd-cal-* vars recolor the calendar's
 * orange (EventCalendar.css); the --theme-* vars reach anything inside that
 * reads themeVar().
 */
export function listingThemeVars(theme: ResolvedPageTheme): Record<string, string> {
  return {
    ...pageThemeVars(theme),
    '--wcwd-cal-orange': theme.accent,
    '--wcwd-cal-orange-hover': `color-mix(in srgb, ${theme.accent} 84%, black)`,
    '--wcwd-cal-orange-tint': `color-mix(in srgb, ${theme.accent} 16%, transparent)`,
    '--wcwd-cal-orange-tint-hover': `color-mix(in srgb, ${theme.accent} 28%, transparent)`,
    // A multi-day bar is small type on a solid fill, so it's held to 5:1
    // like the badges (#2321): the accent where black or white clears it,
    // else deepened. Hover moves the fill away from its text, never toward.
    '--wcwd-cal-bar': theme.badgeSolid,
    '--wcwd-cal-bar-hover':
      theme.badgeOnAccent === WHITE
        ? `color-mix(in srgb, ${theme.badgeSolid} 84%, black)`
        : `color-mix(in srgb, ${theme.badgeSolid} 88%, white)`,
    '--wcwd-cal-on-accent': theme.badgeOnAccent,
  };
}

/** Badge text outside a themed page: orange-on-tint, held to 5:1 (#2321). */
export const DEFAULT_BADGE_TEXT = ensureContrast(COLORS.orangeOnTint, badgeBackground(COLORS.orange), MIN_BADGE_CONTRAST);

/** Defaults outside a themed page: today's WCWD look. */
const FALLBACKS = {
  accent: COLORS.orange,
  'accent-hover': COLORS.orangeHover,
  'accent-text': COLORS.orange,
  'on-accent': COLORS.textOnAccent,
  'white-text-fill': COLORS.orange,
  'white-badge-fill': whiteBadgeFill(COLORS.orange),
  'badge-text': DEFAULT_BADGE_TEXT,
  'badge-solid': badgeSolidFill(COLORS.orange),
  'badge-on-accent': strongestOn(badgeSolidFill(COLORS.orange)),
  text: COLORS.textPrimary,
  border: COLORS.border,
} as const;

/**
 * `themeVar('accent')` -> "var(--theme-accent, #EA580C)". Components use this
 * so they're themed inside a ThemeScope and unchanged everywhere else.
 */
export function themeVar(name: keyof typeof FALLBACKS): string {
  return `var(--theme-${name}, ${FALLBACKS[name]})`;
}

/**
 * The accent at an opacity: themeAlpha(0.1) is rgba(234, 88, 12, 0.1) outside
 * a themed page and the organizer's accent at 10% inside one.
 */
export function themeAlpha(alpha: number): string {
  return `color-mix(in srgb, ${themeVar('accent')} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

/**
 * A ticket or pass with seats in the cart (#2337, #2356): it glows from its
 * corner in the theme's accent, over the card's own fill, with an accent edge.
 * One look for folded rows and open cards, so a pick reads the same either way.
 */
export function pickedSurface(baseBackground: string): { background: string; border: string } {
  return {
    background: `linear-gradient(135deg, ${themeAlpha(0.34)} 0%, ${themeAlpha(0.12)} 60%, transparent 100%), ${baseBackground}`,
    border: `1px solid ${themeAlpha(0.55)}`,
  };
}

/**
 * The solid accent fill that carries white text (#2337, #2344), as the
 * site's diagonal gradient: the theme's white-text fill into a step darker.
 * For a component that paints its own fill, so it doesn't hand-build one on
 * the raw accent, where white can't read on a light theme.
 */
export function whiteTextGradient(): string {
  const fill = themeVar('white-text-fill');
  return `linear-gradient(135deg, ${fill} 0%, color-mix(in srgb, ${fill} 84%, black) 100%)`;
}

/**
 * Accent text on an accent tint: COLORS.orangeOnTint (AA on a 10% orange
 * wash) outside a themed page, the theme's readable accent inside one.
 */
export function themeOnTint(): string {
  return `var(--theme-accent-text, ${COLORS.orangeOnTint})`;
}

/**
 * The black wash laid over a template where white text sits on it in a small
 * space (an organizer's chip). Templates are dark, but a gradient can end
 * bright (Orange Sunset ends on #EA580C, 3.18:1 with white); at 30% every
 * template's every stop holds white at 4.5:1 or better. A test checks them all.
 */
export const TEXT_SCRIM_ALPHA = 0.3;

/** A template background with the text scrim over it, as one CSS background. */
export function scrimmedBackground(background: string): string {
  const wash = `rgba(0, 0, 0, ${TEXT_SCRIM_ALPHA})`;
  return `linear-gradient(${wash}, ${wash}), ${background}`;
}
